import React, { useState, useEffect, useCallback } from 'react';
import { Form, Checkbox, Message, Label, Header, Icon, Button, Input, Divider } from 'semantic-ui-react';
import { SearchSongCycleVersions } from '../common/SearchSongCycleVersions';
import { RuleEntry } from '../common/RuleEntry';
import { RangeEntry } from '../views/RangeEntry';
import { SearchInstruments } from '../common/SearchInstruments';
import { SearchVoiceTypes } from '../common/SearchVoiceTypes';


import debounce from 'lodash/debounce';

export const FormSongVersion = ({data, arrangerAction, activeTab, returnback, instrumentMap, vtMap,
newTranslatorProfiles, newAuthorProfiles, translatorProfileIdsToRemove, authorProfileIdsToRemove}) => {

  const [ availableArrangers, setAvailableArrangers ] = useState([]);
  const [ availableAuthors, setAvailableAuthors ] = useState([]);
  const [ selectedAuthor, setSelectedAuthor ] = useState({});
  const [ availableTranslators, setAvailableTranslators ] = useState([]);
  const [ selectedTranslator, setSelectedTranslator ] = useState({});

  const [ newAudioUrl, setNewAudioUrl ] = useState("");
  const [ newPdfUrl, setNewPdfUrl ] = useState("");
  const [ newPurchaseUrl, setNewPurchaseUrl ] = useState("");
  const [ newScoreImageUrl, setNewScoreImageUrl ] = useState("");


  const inputHandler = (e, { name, value }) => {
    if (name === "duration") {
      returnback(name, Number(value)); // force numeric
    } else {
      returnback(name, value); // leave others as-is
    }
  };


  const instrumentationHandler = (value) => {
    returnback("instrumentation_string", value);
  };

  const voiceTypeHandler = (value) => {
    returnback("voice_type_string", value);
  };

  const rangeHandler = (value) => {
    returnback("ranges", value);
  }

  const generateProfileSearcher = (setter) => {
    return (e, {searchQuery}) => {
      const q = searchQuery.split(" ").map((part) => {
        return `last_name:*${part}*%20OR%20first_name:*${part}*`;
      }).join("%20AND%20");
      fetch(`/go/api/v1/profiles/search?q=${q}`)
      .then((result) => {
        result.json().then((json) => {
          if (!json.hits) {
            return;
          }
          const profiles = json.hits.hits.map((hit) => {
            return {value: JSON.stringify(hit._source), text: `${hit._source.first_name} ${hit._source.last_name}`, key: hit._source.id};
          });
          const keysSeen = [];
          const dedupeProfiles = [];
          profiles.forEach(p => {
            if (!keysSeen.includes(p.key)) {
              keysSeen.push(p.key);
              dedupeProfiles.push(p);
            }
          });
          setter(dedupeProfiles);
        });
      });
    }
  }

  const arrangerSearchHandler = useCallback(debounce(generateProfileSearcher(setAvailableArrangers), 500), []);
  const authorSearchHandler = useCallback(debounce(generateProfileSearcher(setAvailableAuthors), 500), []);
  const translatorSearchHandler = useCallback(debounce(generateProfileSearcher(setAvailableTranslators), 500), []);

  //console.log("rendering song form", data);

  const general = (
    <Form>
      <Form.Group widths='equal'>
        <Form.Input fluid name="version_name" label='Name of Version' value={data.version_info.version_name} placeholder='Name of Version' 
          onChange={inputHandler}/>
      </Form.Group>
      <Form.Group widths='equal'>
        <Form.Input fluid name="title" label='Title' value={data.version_info.title} placeholder='Title' onChange={inputHandler}/>
        <Form.Input fluid name="alternate_title" label='Alternate title' value={data.version_info.alternate_title}
          placeholder='Alternate title' onChange={inputHandler}/>
        <Form.Input fluid name="duration" label='Duration (in seconds)' value={data.version_info.duration} placeholder='0' onChange={inputHandler}/>
      </Form.Group>
      <Form.Group widths='equal'>
        <Form.Field>
          <label>Instrumentation</label>
          <RuleEntry 
            fluid 
            model='instruments' 
            color='purple' 
            label='Edit Instrumentation' 
            instrumentMap={instrumentMap}
            currentState={data.version_info.instrumentation_string}
            submit={instrumentationHandler}
          />
        </Form.Field>
        <Form.Field>
          <label>Voice types</label>
          <RuleEntry
            fluid
            model='voice-types'
            color='purple'
            label='Edit Voice Types'
            instrumentMap={vtMap}
            currentState={data.version_info.voice_type_string}
            submit={voiceTypeHandler}
          />
        </Form.Field>
        <Form.Field>
          <label>Ranges</label>
          <RangeEntry
            label='Edit Ranges'
            vtMap={vtMap}
            submit={rangeHandler}
            ranges={data.ranges}
          />
        </Form.Field>
      </Form.Group>
      <Form.TextArea name="text" label='Text' value={data.version_info.text} placeholder='Text' rows='10' onChange={inputHandler}/>
      <Form.Field control={Checkbox} label='This text is part of the public domain.' value={data.version_info.public_domain} />
      <Message info>
        By checking this box, I certify that, to the best of my
        knowledge, this text is available in the public domain.
      </Message>
      <Form.Group widths='equal'>
        <Form.TextArea name="commission" label='Commission' value={data.version_info.commission} placeholder='Commission' rows='10'
          onChange={inputHandler}/>
        <Form.Field>
          <label>Dedication</label>
          <Form.TextArea name="dedication"  value={data.version_info.dedication} placeholder='Dedication' rows='10'
            onChange={inputHandler} />
          <Message info>Please enter the text as it appears in the score.</Message>
        </Form.Field>
      </Form.Group>
      <Form.Group widths='equal'>
        <Form.Input fluid name="discography_label" label='Discography label' value={data.version_info.discography_label}
          placeholder='Discography label' onChange={inputHandler}/>
        <Form.Input fluid name="publisher" label='Publisher' value={data.version_info.publisher}
          placeholder='Publisher' onChange={inputHandler}/>
      </Form.Group>

    </Form>
  );

  const arrangerDisplay = !data.arranger || data.arranger.id === 0 ? "No Arranger Listed" : `${data.arranger.first_name} ${data.arranger.last_name}`;
  const arrangerDropdown =  (<div>
    Change to: 
    <Form.Dropdown
    options={availableArrangers}
    disabled={arrangerAction !== "change"}
    search
    selection
    placeholder="Search..."
    noResultsMessage="No results found"
    name="new_arranger_profile_id"
    onSearchChange={arrangerSearchHandler}
    onChange={inputHandler}
    />
  </div>);

  const markAuthorForRemoval = (id) => {
    const copy = JSON.parse(JSON.stringify(authorProfileIdsToRemove));
    copy.push(id);
    returnback("remove_author", copy);
  };

  const restoreAuthor = (id) => {
    const copy = JSON.parse(JSON.stringify(authorProfileIdsToRemove));
    const idx = copy.indexOf(id);
    if (idx > -1) {
      copy.splice(idx, 1);
      returnback("remove_author", copy);
    }
  };

  const addAuthorHandler = (e, {value}) => {
    // console.log("addAuthorHandler", JSON.parse(value));
    setSelectedAuthor(JSON.parse(value));
  }

  const addNewAuthor = (value) => {
    if (!value.id) return;
    const copy = JSON.parse(JSON.stringify(newAuthorProfiles));
    if (copy.every(author => author.id !== value.id)) {
      copy.push(value);
      returnback("add_new_author", copy);
    }
  };

  const removeNewAuthor = (author) => {
    const copy = JSON.parse(JSON.stringify(newAuthorProfiles));
    const filtered = copy.filter((newAuth) => newAuth.id !== author.id);
    returnback("add_new_author", filtered);
  }

  const existingTextAuthors = data.text_authors.map((author) => {
    const marked = authorProfileIdsToRemove.includes(author.id);
    const button = marked ? <Button onClick={() => restoreAuthor(author.id)}>Restore</Button> :
      <Button onClick={() => markAuthorForRemoval(author.id)}>Remove</Button>;
    const icon = marked ? <span><Icon name='delete'/>Marked for deletion</span> : <></>;
    return (<div key={`existing_author_${author.id}`}>
      {author.first_name} {author.last_name} {icon} {button}
    </div>);
  });

  const newTextAuthors = newAuthorProfiles.map((author) => {
    const button = <Button onClick={() => removeNewAuthor(author)}>Remove</Button>;
    return (<div key={`new_author_${author.id}`}>
      {author.first_name} {author.last_name} {button}
    </div>);
  });
  
  const textAuthorsDisplay = (!data.text_authors || data.text_authors.length === 0) ? "No text authors listed" : <div>{existingTextAuthors}</div>;
  const newTextAuthorsDisplay = newAuthorProfiles.length === 0 ? "None" : <div>{newTextAuthors}</div>;
  
  const markXlatorForRemoval = (id) => {
    const copy = JSON.parse(JSON.stringify(translatorProfileIdsToRemove));
    copy.push(id);
    returnback("remove_translator", copy);
  };

  const restoreXlator = (id) => {
    const copy = JSON.parse(JSON.stringify(translatorProfileIdsToRemove));
    const idx = copy.indexOf(id);
    if (idx > -1) {
      copy.splice(idx, 1);
      returnback("remove_translator", copy);
    }
  };

  const addXlatorHandler = (e, {value}) => {
    // console.log("addXlatorHandler", JSON.parse(value));
    setSelectedTranslator(JSON.parse(value));
  }

  const addNewXlator = (value) => {
    if (!value.id) return;
    const copy = JSON.parse(JSON.stringify(newTranslatorProfiles));
    if (copy.every(xlator => xlator.id !== value.id)) {
      copy.push(value);
      returnback("add_new_translator", copy);
    }
  };

  const removeNewXlator = (xlator) => {
    const copy = JSON.parse(JSON.stringify(newTranslatorProfiles));
    const filtered = copy.filter((newXlator) => newXlator.id !== xlator.id);
    returnback("add_new_translator", filtered);
  }

  const existingTranslators = data.translators.map((xlator) => {
    const marked = translatorProfileIdsToRemove.includes(xlator.id);
    const button = marked ? <Button onClick={() => restoreXlator(xlator.id)}>Restore</Button> :
      <Button onClick={() => markXlatorForRemoval(xlator.id)}>Remove</Button>;
    const icon = marked ? <span><Icon name='delete'/>Marked for deletion</span> : <></>;
    return (<div key={`existing_xlator_${xlator.id}`}>
      {xlator.first_name} {xlator.last_name} {icon} {button}
    </div>);
  });

  const newTranslators = newTranslatorProfiles.map((xlator) => {
    const button = <Button onClick={() => removeNewXlator(xlator)}>Remove</Button>;
    return (<div key={`new_xlator_${xlator.id}`}>
      {xlator.first_name} {xlator.last_name} {button}
    </div>);
  });

  const translatorsDisplay = !data.translators || data.translators.length === 0 ? "No translators listed" : <div>{existingTranslators}</div>;
  const newTranslatorsDisplay = newTranslatorProfiles.length === 0 ? "None" : <div>{newTranslators}</div>;

  const people = (
    <Form>
      <Header as='h4' color='black' className='subtitle'>Arranger</Header>
      <Form.Field>
        <Checkbox
          radio
          label={`Retain current: ${arrangerDisplay}`}
          name='arrangerRadioGroup'
          value='retain'
          checked={arrangerAction === "retain"}
          onChange={(e, data) => {
            returnback("arranger_action", data.value);
          }}
        />
      </Form.Field>
      <Form.Field>
        <Checkbox
          radio
          label="Remove arranger"
          name='arrangerRadioGroup'
          value='delete'
          checked={arrangerAction === "delete"}
          onChange={(e, data) => {
            returnback("arranger_action", data.value);
          }}
        />
      </Form.Field>
      <Form.Field>
        <Checkbox
          radio
          label="Change arranger"
          name='arrangerRadioGroup'
          value='change'
          checked={arrangerAction === "change"}
          onChange={(e, data) => {
            returnback("arranger_action", data.value);
          }}
        />
      </Form.Field>
      {arrangerDropdown}
      

      <Header as='h4' color='black' className='subtitle'>Text Authors</Header>
      <div>Currently: {textAuthorsDisplay}</div>
      <div>To be added: {newTextAuthorsDisplay}</div>
      <div>
        Select new author: 
        <Form.Dropdown
        options={availableAuthors}
        search
        selection
        placeholder="Search..."
        noResultsMessage="No results found"
        name="new_text_author"
        onSearchChange={authorSearchHandler}
        onChange={addAuthorHandler}
        />
        <Button onClick={() => addNewAuthor(selectedAuthor)}>Add</Button>
      </div>

      <Header as='h4' color='black' className='subtitle'>Translators</Header>
      <div>Currently: {translatorsDisplay}</div>
      <div>To be added: {newTranslatorsDisplay}</div>
      <div>
        Select new translator:
        <Form.Dropdown
        options={availableTranslators}
        search
        selection
        placeholder="Search..."
        noResultsMessage="No results found"
        name="new_translator"
        onSearchChange={translatorSearchHandler}
        onChange={addXlatorHandler}
        />
        <Button onClick={() => addNewXlator(selectedTranslator)}>Add</Button>
      </div>
    </Form>
  );

  const audioUrls = !data.version_info.audio_urls ? <></> : data.version_info.audio_urls.map((url, i) => {
    const button = <Button onClick={() => {
      const copy = JSON.parse(JSON.stringify(data.version_info.audio_urls))
      copy.splice(i, 1);
      returnback("audio_urls", copy);
    }}>Remove</Button>;
    return (<div key={i}>
      {url} {button}
    </div>);
  });

  const audioUrlsSection = <div>
      <Header as='h4' color='black' className='subtitle'>Audio URLs</Header>
      {audioUrls}
      <Input onChange={(e, {value}) => {
        setNewAudioUrl(value);
      }}/>
      <Button onClick={() => {
        const copy = JSON.parse(JSON.stringify(data.version_info.audio_urls));
        copy.push(newAudioUrl);
        returnback("audio_urls", copy);
      }}>Add</Button>
    </div>;

const pdfUrls = !data.version_info.pdf_urls ? <></> : data.version_info.pdf_urls.map((url, i) => {
  const button = <Button onClick={() => {
    const copy = JSON.parse(JSON.stringify(data.version_info.pdf_urls))
    copy.splice(i, 1);
    returnback("pdf_urls", copy);
  }}>Remove</Button>;
  return (<div key={i}>
    {url} {button}
  </div>);
});

const pdfUrlsSection = <div>
    <Header as='h4' color='black' className='subtitle'>PDF URLs</Header>
    {pdfUrls}
    <Input onChange={(e, {value}) => {
      setNewPdfUrl(value);
    }}/>
    <Button onClick={() => {
      const copy = JSON.parse(JSON.stringify(data.version_info.pdf_urls));
      copy.push(newPdfUrl);
      returnback("pdf_urls", copy);
    }}>Add</Button>
  </div>;

const purchaseUrls = !data.version_info.purchase_urls ? <></> : data.version_info.purchase_urls.map((url, i) => {
  const button = <Button onClick={() => {
    const copy = JSON.parse(JSON.stringify(data.version_info.purchase_urls))
    copy.splice(i, 1);
    returnback("purchase_urls", copy);
  }}>Remove</Button>;
  return (<div key={i}>
    {url} {button}
  </div>);
});

const purchaseUrlsSection = <div>
    <Header as='h4' color='black' className='subtitle'>Purchase URLs</Header>
    {purchaseUrls}
    <Input onChange={(e, {value}) => {
      setNewPurchaseUrl(value);
    }}/>
    <Button onClick={() => {
      const copy = JSON.parse(JSON.stringify(data.version_info.purchase_urls));
      copy.push(newPurchaseUrl);
      returnback("purchase_urls", copy);
    }}>Add</Button>
  </div>;

const scoreImageUrls = !data.version_info.score_image_urls ? <></> : data.version_info.score_image_urls.map((url, i) => {
  const button = <Button onClick={() => {
    const copy = JSON.parse(JSON.stringify(data.version_info.score_image_urls))
    copy.splice(i, 1);
    returnback("score_image_urls", copy);
  }}>Remove</Button>;
  return (<div key={i}>
    {url} {button}
  </div>);
});

const scoreImageUrlsSection = <div>
    <Header as='h4' color='black' className='subtitle'>Score Image URLs</Header>
    {scoreImageUrls}
    <Input onChange={(e, {value}) => {
      setNewScoreImageUrl(value);
    }}/>
    <Button onClick={() => {
      const copy = JSON.parse(JSON.stringify(data.version_info.score_image_urls));
      copy.push(newScoreImageUrl);
      returnback("score_image_urls", copy);
    }}>Add</Button>
  </div>;

  const links = (
    <Form>
      {audioUrlsSection}
      <Divider hidden/>
      {pdfUrlsSection}
      <Divider hidden/>
      {purchaseUrlsSection}
      <Divider hidden/>
      {scoreImageUrlsSection}

    </Form>
  );

  const dates = (
    <Form>
      Time format 1948-01-01T00:00:00+00:00
      <Form.Group widths='equal'>
        <Form.Input fluid label='Composition start date' value={data.version_info.composition_start_date} placeholder='Composition start date' />
        <Form.Input fluid label='Composition end date' value={data.version_info.composition_end_date} placeholder='Composition end date'/> 
      </Form.Group>
      <Form.Group widths='equal'>
        <Form.Input fluid label='Premiere date' value={data.version_info.premiere_date} placeholder='Premiere date' />
        <Form.Input fluid label='Text publication date' value={data.version_info.text_publication_date} placeholder='Text publication date' />
      </Form.Group>
    </Form>
  );

  //const places = null;

  const administrative = (
    <pre className='as-is'><code>{JSON.stringify(data, null, 2)}</code></pre>
  );

  //console.log(data);

  switch (activeTab.toLowerCase()) {
    case 'people':
      return people;
    case 'links':
      return links;
    case 'dates':
      return dates;
    // case 'places':
    //   return places;
    case 'administrative':
      return administrative;
    case 'general':
    default:
      return general;
  }
};
